import { Flex, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { CSSProperties, ReactNode } from 'react';

export const INFO_COLUMN_MAX_WIDTH = 200;
export const DESCRIPTION_COLUMN_MAX_WIDTH = 300;
const DESCRIPTION_PREVIEW_CHARS = 80;

export const ACTION_ICON_COLORS = {
  view: 'var(--evimo-blue, #1E6DF6)',
  edit: 'var(--evimo-warning, #ec7e00)',
  delete: 'var(--evimo-danger, #e23b4a)',
  reopen: 'var(--evimo-success, #16a34a)',
  resolve: 'var(--evimo-success, #16a34a)',
  ignore: 'var(--evimo-text-muted, #6b7280)',
  unignore: 'var(--evimo-blue, #1E6DF6)',
} as const;

const POINTER_CURSOR_STYLE = { cursor: 'pointer' } as const;

export function withPointerCursor<T>(columns: ColumnsType<T>): ColumnsType<T> {
  return columns.map((column) => {
    const previousOnHeaderCell = column.onHeaderCell;
    const previousOnCell = column.onCell;

    return {
      ...column,
      onHeaderCell: (col) => {
        const previous =
          typeof previousOnHeaderCell === 'function'
            ? previousOnHeaderCell(col)
            : (previousOnHeaderCell ?? {});
        return {
          ...previous,
          style: { ...previous.style, ...POINTER_CURSOR_STYLE },
        };
      },
      onCell: (record, index) => {
        const previous =
          typeof previousOnCell === 'function'
            ? previousOnCell(record, index)
            : (previousOnCell ?? {});
        return {
          ...previous,
          style: { ...previous.style, ...POINTER_CURSOR_STYLE },
        };
      },
    };
  }) as ColumnsType<T>;
}

/**
 * Centers action icons in a fixed track so 3- and 4-icon rows stay aligned.
 * Sized for four compact text buttons (~32px each + gaps).
 */
export const ACTIONS_CLUSTER_WIDTH = 152;

export function renderCenteredActions(children: ReactNode): ReactNode {
  return (
    <Flex justify="center" align="center" style={{ width: '100%' }}>
      <div
        style={{
          display: 'inline-flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: ACTIONS_CLUSTER_WIDTH,
          minHeight: 32,
        }}
      >
        {children}
      </div>
    </Flex>
  );
}

export type TruncateWidth = number | 'fill';

const truncatedCellStyle = (maxWidth: TruncateWidth): CSSProperties =>
  maxWidth === 'fill'
    ? {
        display: 'block',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }
    : {
        maxWidth,
        display: 'inline-block',
        verticalAlign: 'middle',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      };

export function renderEmptyCell(): ReactNode {
  return (
    <Typography.Text type="secondary" style={{ opacity: 0.55 }}>
      —
    </Typography.Text>
  );
}

export function renderErrorCodeCell(value: string): ReactNode {
  return (
    <Typography.Text
      code
      style={{
        color: 'var(--evimo-blue)',
        background: 'color-mix(in srgb, var(--evimo-blue) 8%, transparent)',
        border: 'none',
      }}
    >
      {value}
    </Typography.Text>
  );
}

export function renderVendorErrorCodeCell(
  value: string | null | undefined,
  maxWidth: TruncateWidth = 'fill',
): ReactNode {
  if (!value) {
    return renderEmptyCell();
  }
  return (
    <Tooltip title={value} placement="topLeft" mouseEnterDelay={0.2}>
      <Typography.Text
        type="secondary"
        code
        style={{
          ...truncatedCellStyle(maxWidth),
          margin: 0,
        }}
      >
        {value}
      </Typography.Text>
    </Tooltip>
  );
}

/** Rounded pill for occurrence counts in the unresolved queue. */
export function renderOccurrenceCountCell(
  value: number | null | undefined,
): ReactNode {
  const count = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 28,
        height: 28,
        paddingInline: count >= 100 ? 10 : 8,
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1,
        color: 'var(--evimo-blue, #1E6DF6)',
        background:
          'color-mix(in srgb, var(--evimo-blue, #1E6DF6) 12%, transparent)',
        border:
          '1px solid color-mix(in srgb, var(--evimo-blue, #1E6DF6) 22%, transparent)',
      }}
    >
      {count}
    </span>
  );
}

/** Short diagnostic snippet — truncated with full-text tooltip. */
export function renderInfoCell(
  value: string | null | undefined,
  maxWidth: TruncateWidth = INFO_COLUMN_MAX_WIDTH,
): ReactNode {
  if (!value) {
    return renderEmptyCell();
  }
  return (
    <Tooltip title={value} placement="topLeft" mouseEnterDelay={0.2}>
      <Typography.Text
        style={{
          ...truncatedCellStyle(maxWidth),
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--evimo-text, #1a1d23)',
        }}
      >
        {value}
      </Typography.Text>
    </Tooltip>
  );
}

/** Human-readable description — truncated with full-text tooltip. */
export function renderDescriptionCell(
  value: string | null | undefined,
  maxWidth: TruncateWidth = DESCRIPTION_COLUMN_MAX_WIDTH,
): ReactNode {
  if (!value) {
    return renderEmptyCell();
  }

  const useCssEllipsis = maxWidth === 'fill';
  const isLong = value.length > DESCRIPTION_PREVIEW_CHARS;
  const preview =
    useCssEllipsis || !isLong
      ? value
      : `${value.slice(0, DESCRIPTION_PREVIEW_CHARS).trimEnd()}...`;

  return (
    <Tooltip title={value} placement="topLeft" mouseEnterDelay={0.2}>
      <Typography.Text
        type="secondary"
        style={{
          ...truncatedCellStyle(maxWidth),
          fontSize: 13,
          lineHeight: 1.45,
          borderLeft:
            '2px solid color-mix(in srgb, var(--evimo-blue, #1E6DF6) 45%, transparent)',
          paddingLeft: 10,
        }}
      >
        {preview}
      </Typography.Text>
    </Tooltip>
  );
}
